import { Component, Inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

export interface DateTimePickerData {
  title?: string;
  initialDate?: Date;
}

export interface DateTimePickerResult {
  selectedDateTime: Date;
}

@Component({
  selector: 'lib-verkada-datetime-picker-modal',
  templateUrl: './verkada-datetime-picker-modal.component.html',
  styleUrls: ['./verkada-datetime-picker-modal.component.scss'],
})
export class VerkadaDatetimePickerModalComponent implements OnInit {
  dateTimeForm: FormGroup;
  timeControl: FormControl;
  maxAllowedDateTime: Date;

  constructor(
    public dialogRef: MatDialogRef<VerkadaDatetimePickerModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DateTimePickerData,
  ) {
    this.maxAllowedDateTime = new Date();
    const initialDate = this.data.initialDate || new Date();
    let initialTime: string;
    if (this.isToday(initialDate) && initialDate > this.maxAllowedDateTime) {
      initialTime = this.formatTimeFor24Hour(this.maxAllowedDateTime);
    } else {
      initialTime = this.formatTimeFor24Hour(initialDate);
    }
    this.timeControl = new FormControl(initialTime, [
      Validators.required,
      Validators.pattern(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/),
      this.timeValidator.bind(this),
    ]);
    let initialDateValue = initialDate;
    if (this.isToday(initialDate) && initialDate > this.maxAllowedDateTime) {
      initialDateValue = new Date(this.maxAllowedDateTime);
    }
    this.dateTimeForm = new FormGroup({
      date: new FormControl(initialDateValue, [Validators.required]),
      time: this.timeControl,
    });
    this.dateTimeForm.get('date')?.valueChanges.subscribe(() => {
      this.timeControl.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.enforceMaxAllowedDateTime();
  }

  dateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    const maxAllowedDate = new Date(this.maxAllowedDateTime);
    maxAllowedDate.setHours(23, 59, 59, 999); // End of max allowed day
    return date <= maxAllowedDate;
  };

  onDateSelected(selectedDate: Date | null): void {
    if (!selectedDate) return;
    this.dateTimeForm.get('date')?.setValue(selectedDate);
    this.enforceMaxAllowedDateTime();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (this.dateTimeForm.valid) {
      const selectedDate = this.dateTimeForm.get('date')?.value;
      const selectedTime = this.timeControl.value;
      const combinedDateTime = this.combineDateTime(selectedDate, selectedTime);
      const result: DateTimePickerResult = { selectedDateTime: combinedDateTime };
      this.dialogRef.close(result);
    }
  }

  private formatTimeFor24Hour(date: Date): string {
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  private combineDateTime(date: Date, timeString: string): Date {
    const combinedDateTime = new Date(date);
    const timeRegex = /^(\d{1,2}):(\d{2})$/;
    const match = timeString.match(timeRegex);
    if (match) {
      const hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      combinedDateTime.setHours(hours, minutes, 0, 0);
    }
    return combinedDateTime;
  }

  private enforceMaxAllowedDateTime(): void {
    const selectedDate = this.dateTimeForm.get('date')?.value;
    if (!selectedDate) return;
    if (this.isSameDay(selectedDate, this.maxAllowedDateTime)) {
      const currentTime = this.timeControl.value;
      const maxAllowedTime = this.formatTimeFor24Hour(this.maxAllowedDateTime);
      if (this.timeControl.errors?.['timeExceedsLimit'] || currentTime > maxAllowedTime) {
        this.timeControl.setValue(maxAllowedTime);
      }
    }
  }

  private timeValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    const selectedDate = this.dateTimeForm?.get('date')?.value;
    if (!selectedDate) return null;
    if (!this.isSameDay(selectedDate, this.maxAllowedDateTime)) {
      return null;
    }
    const maxAllowedTime = this.formatTimeFor24Hour(this.maxAllowedDateTime);
    if (control.value > maxAllowedTime) {
      return {
        timeExceedsLimit: {
          message: `The latest time you can select is: ${maxAllowedTime}`,
          maxTime: maxAllowedTime,
        },
      };
    }
    return null;
  }

  private isToday(date: Date): boolean {
    const today = new Date();
    return this.isSameDay(date, today);
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  }
}
